import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatStatus } from "ai";
import {
  fetchFollowUpSuggestionsCapability,
  generateFollowUpSuggestions,
  readFollowUpSuggestions,
  type FollowUpSuggestion,
  type FollowUpSuggestionsEnvelope,
} from "@/api/follow-up-suggestions";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import type { AccessTokenGetter } from "@/lib/utils";

export interface UseFollowUpSuggestionsOptions {
  sessionId: string;
  lastAssistantMessageId: string | null;
  completedSuccessfully: boolean;
  writable: boolean;
  status: ChatStatus;
  getToken: AccessTokenGetter;
  bootstrapCapability?: number;
  bootstrapSuggestions?: FollowUpSuggestionsEnvelope | null;
  bootstrapPending?: boolean;
}

export function useFollowUpSuggestions({ sessionId, lastAssistantMessageId, completedSuccessfully, writable, status, getToken, bootstrapCapability, bootstrapSuggestions, bootstrapPending = false }: UseFollowUpSuggestionsOptions) {
  const reconnectKey = useBackendReconnectKey();
  const [result, setResult] = useState<{ key: string; suggestions: FollowUpSuggestion[] } | null>(null);
  const completed = useRef(new Map<string, FollowUpSuggestion[]>());
  const attempted = useRef(new Set<string>());
  const readAttempted = useRef(new Map<string, number>());
  const dismissed = useRef(new Set<string>());
  const lastGeneration = useRef(new Map<string, number>());
  const inFlight = useRef<string | null>(null);
  const active = useRef<string | null>(null);
  const key = sessionId && lastAssistantMessageId ? `${sessionId}:${lastAssistantMessageId}` : null;
  const enabled = Boolean(key && writable && completedSuccessfully && status === "ready");
  // The render guard hides old chips before the effect has a chance to abort a stale request.
  const suggestions = enabled && result?.key === key ? result.suggestions : [];

  const clear = useCallback(() => {
    if (key) { completed.current.set(key, []); dismissed.current.add(key); }
    setResult(null);
  }, [key]);

  useEffect(() => {
    active.current = enabled ? key : null;
    setResult(null);
    if (!enabled || !key || !lastAssistantMessageId || bootstrapPending) return;
    if (readAttempted.current.get(key) === reconnectKey || inFlight.current === key) {
      const cached = completed.current.get(key);
      if (cached) setResult({ key, suggestions: cached });
      return;
    }
    readAttempted.current.set(key, reconnectKey);
    const controller = new AbortController();
    inFlight.current = key;
    const current = () => !controller.signal.aborted && active.current === key;
    void (async () => {
      const capability = bootstrapCapability === undefined
        ? await fetchFollowUpSuggestionsCapability(sessionId, getToken, controller.signal)
        : { follow_up_suggestions: bootstrapCapability };
      if (!current() || capability.follow_up_suggestions !== 1) return;
      const read = bootstrapSuggestions?.message_id === lastAssistantMessageId
        ? bootstrapSuggestions
        : await readFollowUpSuggestions(sessionId, lastAssistantMessageId, getToken, controller.signal);
      if (!current() || !read || read.capability_version !== 1) return;
      if (read.stored) {
        const suggestions = dismissed.current.has(key) ? [] : read.suggestions;
        completed.current.set(key, suggestions);
        setResult({ key, suggestions });
        return;
      }
      if (completed.current.has(key) || attempted.current.has(key)) return;
      if (Date.now() - (lastGeneration.current.get(sessionId) ?? -Infinity) < 15_000) return;
      attempted.current.add(key);
      lastGeneration.current.set(sessionId, Date.now());
      const generated = await generateFollowUpSuggestions(sessionId, lastAssistantMessageId, getToken, controller.signal);
      if (!current() || !generated) return;
      // A throttled stored:false response is final for this mount; never retry it.
      completed.current.set(key, generated.suggestions);
      setResult({ key, suggestions: generated.suggestions });
    })().finally(() => { if (inFlight.current === key) inFlight.current = null; });
    return () => {
      controller.abort();
      if (active.current === key) active.current = null;
      if (inFlight.current === key) inFlight.current = null;
    };
  }, [key, sessionId, lastAssistantMessageId, enabled, getToken, reconnectKey, bootstrapCapability, bootstrapSuggestions, bootstrapPending]);

  return { suggestions, clear };
}
